import React, { useContext } from 'react';

import { Box, Button, MenuItem, MenuList, Typography } from '@material-ui/core';
import { GridColDef } from '@mui/x-data-grid';
import { AiOutlinePlus } from 'react-icons/ai';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';

import {
  AppA,
  AppDataGrid,
  FilterSystem,
  GridActions,
  GridCellWithAvatar,
  ModuleOnboardingChips,
} from '@vyce/core/src/components';
import { GRID_PAGE_SIZE } from '@vyce/core/src/constants';
import { getUKFormattedDate } from '@vyce/core/src/utils/dates';
import { useHorizontalScrollStyles } from '@vyce/core/src/styles';
import { AppSearchInput } from '@vyce/core/src/components/inputs';
import { DeviceContext } from '@vyce/core/src/contexts';
import useChatRequests from '@vyce/core/src/hooks/useChatRequests';
import { usePermissions } from '@vyce/core/src/hooks/usePermissions';
import { INVITE_WORKERS_PERMISSIONS, InviteListItem } from '@vyce/core/src/types';
import { useInviteFilters, useInvitesData } from './hooks';
import { useStyles } from './styles';
import { InviteProps } from './types';
import { getAvatar } from '../../utils';
import { useInviteStatusOptions } from './hooks/useInviteStatusOptions';
import { InviteDetails } from './components';
import { canResendInvite, getInviteModules, isDirectAddInvite } from './config';

export const Invites = ({
  inviteButton,
  height = 'calc(100vh - 250px)',
  hideFilters = false,
  ...restProps
}: InviteProps) => {
  const classes = useStyles();
  const horizontalScrollClasses = useHorizontalScrollStyles();
  const { isMobile } = useContext(DeviceContext);
  const { canAny } = usePermissions();
  // Same pair the backend checks for inviting, resending and retrying
  const canInvite = canAny(INVITE_WORKERS_PERMISSIONS);
  const history = useHistory();
  const { statusOptions } = useInviteStatusOptions();
  const { createPrivateChat } = useChatRequests();
  const { selectedCompanyId, chatUser, chatCompanyId, chatRoute, statuses } = restProps;
  const { filters, filtersSections, handleFilterChange } = useInviteFilters({
    selectedCompanyId,
    fetchFilterResources: restProps.fetchFilterResources,
    fetchFilterInviters: restProps.fetchFilterInviters,
    hasFixedStatuses: !!statuses,
  });

  const goToChat = async (row: {
    user_id?: string;
    full_name?: string;
    email?: string;
    phone?: string;
    avatar?: string;
  }) => {
    const companyId = chatCompanyId ?? selectedCompanyId;
    if (!chatUser || !companyId || !row.user_id) {
      return;
    }
    await createPrivateChat({
      creator: chatUser,
      chatUser: {
        uid: row.user_id,
        name: row.full_name ?? '',
        email: row.email,
        phone: row.phone,
        avatarUrl: row.avatar,
      },
      companyId,
      messagesRoute: chatRoute,
    });
  };

  const {
    loading,
    invitedUsers,
    page,
    sortModel,
    total,
    handleInviteButtonClick,
    handleSortModelChange,
    handlePageChange,
    resendInvite,
    deleteInvite,
    retryInviteItem,
    retryingItemIds,
    handleSearchChange,
  } = useInvitesData(restProps, filters);

  const columns: GridColDef[] = [
    {
      field: 'full_name',
      headerName: 'Name',
      flex: 0.1,
      minWidth: 250,
      disableColumnMenu: true,
      renderCell: params =>
        params.row.full_name?.trim() ? (
          <GridCellWithAvatar
            avatarUrl={params.row.avatar}
            avatarPlaceholder={getAvatar(params.row.gender)}
            name={params.row.full_name}
            link={`${history.location.pathname}/${params.row.full_name.split(' ')?.[0]}_${
              params.row?.user_id
            }`}
            createPrivateChat={chatUser && params.row.user_id ? () => goToChat(params.row) : undefined}
          />
        ) : (
          <GridCellWithAvatar avatarPlaceholder={getAvatar(params.row.gender)} name="-" />
        ),
    },
    {
      field: 'email_and_phone',
      headerName: 'Invite email/ phone number',
      flex: 0.1,
      minWidth: 250,
      sortable: false,
      disableColumnMenu: true,
      renderCell: params =>
        // A direct add was never sent anywhere — it has no email or phone, only who added them
        isDirectAddInvite(params.row as InviteListItem) ? (
          <Typography className={classes.secondaryCellText}>
            Added directly
            {params.row.created_by_full_name ? ` by ${params.row.created_by_full_name}` : ''}
          </Typography>
        ) : (
          <AppA
            content={params.row.email || params.row.phone}
            href={params.row.email ? `mailto: ${params.row.email}` : `tel: ${params.row.phone}`}
          />
        ),
    },
    {
      field: 'company_name',
      headerName: 'Company',
      flex: 0.1,
      disableColumnMenu: true,
      minWidth: 110,
    },
    {
      field: 'modules',
      headerName: 'Modules & Onboarding',
      flex: 0.15,
      disableColumnMenu: true,
      sortable: false,
      minWidth: 200,
      // The pill's hover card carries the whole story: onboarding per module, then every
      // place the invite puts the person on
      renderCell: params => {
        const invite = params.row as InviteListItem;
        return (
          <ModuleOnboardingChips
            modules={getInviteModules(invite)}
            onboarding={invite.user_id ? invite.onboarding : null}
            emptyTooltip="Not started"
            tooltipExtra={
              invite.items?.length ? (
                <InviteDetails
                  invite={invite}
                  // Retrying is inviting again: same rights as the invite itself
                  onRetry={canInvite ? retryInviteItem : undefined}
                  retryingItemIds={retryingItemIds}
                />
              ) : undefined
            }
          />
        );
      },
    },
    {
      field: 'invite_type',
      headerName: 'Invite Type',
      flex: 0.15,
      disableColumnMenu: true,
      sortable: false,
      minWidth: 120,
      valueGetter: params => {
        const inviteType = params.row?.invite_type;

        if (inviteType === 'staff') {
          return 'Admin';
        }

        return inviteType ? inviteType.charAt(0).toUpperCase() + inviteType.slice(1) : inviteType;
      },
    },
    {
      field: 'created_by_full_name',
      headerName: 'Invited by',
      flex: 0.07,
      disableColumnMenu: true,
      sortable: false,
      minWidth: 120,
    },
    {
      field: 'created_at',
      headerName: 'Invite Date',
      flex: 0.07,
      disableColumnMenu: true,
      valueGetter: params => getUKFormattedDate(params.row?.created_at),
      minWidth: 120,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.07,
      minWidth: 170,
      renderCell: params => {
        const option = statusOptions[params.row.status as keyof typeof statusOptions];
        return (
          <Box display="flex" alignItems="center" gridGap={12}>
            {option?.icon}
            <Typography className={classes.customTextCell}>{option?.text ?? params.row.status}</Typography>
          </Box>
        );
      },
    },
    {
      field: '',
      headerName: 'Actions',
      width: 80,
      hideSortIcons: true,
      sortable: false,
      disableColumnMenu: true,
      renderCell: params =>
        // Resending and cancelling an invite are both invite management, so
        // without the right the button goes rather than opening an empty menu.
        canInvite ? (
          <Box display="flex" width="100%">
            <GridActions>
              <MenuList>
                {/* A replaced invite or a direct add can't be resent — the server answers 409 */}
                {canResendInvite(params.row as InviteListItem) && (
                  <MenuItem onClick={() => resendInvite(params.row.uuid, params.row.company_id)}>
                    Resend invite
                  </MenuItem>
                )}
                <MenuItem onClick={() => deleteInvite(params.row.uuid, params.row.company_id)}>
                  Cancel invitation
                </MenuItem>
              </MenuList>
            </GridActions>
          </Box>
        ) : null,
    },
  ];

  return (
    <>
      <Box
        mb={2}
        gridGap={16}
        className={clsx(horizontalScrollClasses.blockWrapper, horizontalScrollClasses.blockWithHideScroll)}>
        <Box>
          <AppSearchInput onChange={handleSearchChange} isSmall smallRounding expanded={!isMobile} />
        </Box>

        <Box display="flex" justifyContent="flex-end" gridGap={16}>
          {!hideFilters && (
            <FilterSystem filtersSections={filtersSections} onFiltersChange={handleFilterChange} />
          )}
          {inviteButton
            ? React.createElement(inviteButton)
            : canInvite && (
                <Button
                  onClick={handleInviteButtonClick}
                  size="small"
                  data-tour="invite"
                  startIcon={<AiOutlinePlus />}
                  variant="contained"
                  color="primary">
                  Invite New Members
                </Button>
              )}
        </Box>
      </Box>

      <AppDataGrid
        rows={invitedUsers}
        noPaper
        height={height}
        getRowId={row => row.uuid}
        columns={columns}
        loading={loading}
        pageSize={GRID_PAGE_SIZE}
        paginationMode="server"
        sortingMode="server"
        sortModel={sortModel}
        page={page}
        rowCount={total}
        onSortModelChange={handleSortModelChange}
        onPageChange={handlePageChange}
        rowsPerPageOptions={[GRID_PAGE_SIZE]}
        disableSelectionOnClick
        unit="invites"
        withProfileButton
      />
    </>
  );
};
